import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Col, Row, Skeleton, Typography } from "antd";
import { useGetBotBillingQuery } from "api";
import { useAppSelector } from "store/hooks";
import { getRouteByName } from "libs";
import { BotsBillingDetailsForm } from "./BotsBillingDetailsForm";
import { BotsBillingInvoicesList } from "./BotsBillingInvoicesList";
import { BotsBillingPaymentSection } from "./BotsBillingPaymentSection";
import { BotsBillingSubscriptionCard } from "./BotsBillingSubscriptionCard";

/**
 * Composite billing page for the SPOR NETWORK platform owner.
 *
 * Routes:
 *   /bots/billing → this page (superadmin only, gated by `bots.billing.manage` permission).
 *
 * Required env: REACT_APP_STRIPE_PUBLISHABLE_KEY (loaded by libs/stripe.ts on
 * the first card-add interaction). The publishable key is safe to expose; the
 * secret key lives only on the sporapi backend.
 */
export const BotsBillingPage = () => {
  const navigate = useNavigate();
  const { isAll, isBotsBillingManage } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions
  );

  const { data, isLoading, error } = useGetBotBillingQuery(undefined);

  useEffect(() => {
    if (!isAll && !isBotsBillingManage) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isAll, isBotsBillingManage, navigate]);

  // Diagnostic — surface the real RTK Query error so we can tell
  // 401 vs 403 vs 500 vs JSON-shape mismatch. The fallback Alert
  // below hid the actual cause behind a generic message.
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.log("[BotsBillingPage] getBotBilling failed", error);
    }
    if (!isLoading && !error && !data) {
      // eslint-disable-next-line no-console
      console.log("[BotsBillingPage] getBotBilling returned no data", data);
    }
  }, [error, data, isLoading]);

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  if (error || !data) {
    const errStatus =
      (error as { status?: number | string } | undefined)?.status;
    const errBody = (error as { data?: unknown } | undefined)?.data;
    return (
      <Alert
        type="error"
        showIcon
        message="Nu am putut incarca datele de facturare."
        description={
          <>
            <div>
              Verifica ca platform_settings exista si ca rularea
              bots:bootstrap-stripe a fost facuta cu succes.
            </div>
            {errStatus !== undefined && (
              <div style={{ marginTop: 8, fontFamily: "monospace" }}>
                Status: {String(errStatus)}
              </div>
            )}
            {errBody !== undefined && (
              <pre
                style={{
                  marginTop: 8,
                  background: "#1f1f1f",
                  color: "#fff",
                  padding: 8,
                  borderRadius: 4,
                  fontSize: 12,
                  maxHeight: 200,
                  overflow: "auto",
                }}>
                {typeof errBody === "string"
                  ? errBody
                  : JSON.stringify(errBody, null, 2)}
              </pre>
            )}
          </>
        }
      />
    );
  }

  const { billing_details, subscription, current_quote, has_payment_method } =
    data.data;

  const isInactive = billing_details.bots_enabled === false;
  const needsCustomerSetup = !billing_details.has_stripe_customer;
  const needsPaymentMethod =
    billing_details.has_stripe_customer && !has_payment_method;

  return (
    <>
      <Typography.Title level={3} style={{ marginBottom: 16 }}>
        Facturare boti
      </Typography.Title>

      {isInactive && (
        <Alert
          type="warning"
          showIcon
          message="Sistemul de boti este oprit global"
          description="bots_enabled = false in platform_settings. Niciun bot nu va actiona, indiferent de starea abonamentului."
          style={{ marginBottom: 16 }}
        />
      )}

      {needsCustomerSetup && (
        <Alert
          type="info"
          showIcon
          message="Pas 1: completeaza datele de facturare"
          description="Pana cand inregistram clientul in Stripe, nu putem accepta plata, deci nu se pot crea boti."
          style={{ marginBottom: 16 }}
        />
      )}

      {needsPaymentMethod && (
        <Alert
          type="info"
          showIcon
          message="Pas 2: adauga un card"
          description="Datele de facturare sunt salvate. Adauga o metoda de plata pentru a putea crea boti."
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <BotsBillingSubscriptionCard
            subscription={subscription}
            currentQuote={current_quote}
          />
        </Col>
        <Col xs={24} lg={10}>
          <BotsBillingPaymentSection
            hasStripeCustomer={billing_details.has_stripe_customer}
          />
        </Col>

        <Col span={24}>
          <BotsBillingDetailsForm details={billing_details} />
        </Col>

        {billing_details.has_stripe_customer && (
          <Col span={24}>
            <BotsBillingInvoicesList />
          </Col>
        )}
      </Row>
    </>
  );
};
